/**
 * TrainerDashboard — mobile-first home screen for the Formateur role.
 *
 * Mobile layout: stacked KPI cards (2 cols on phone, 4 on tablet) + sectioned
 * lists for upcoming sessions, recent evaluations, and expiring documents.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/client';
import { Calendar, Clock, Users, Star, AlertCircle, ArrowRight, Loader2, FileWarning, Sparkles } from 'lucide-react';

export default function TrainerDashboard() {
  const [data, setData] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/me/trainer/dashboard').then((r) => r.data),
      api.get('/me/trainer/upcoming-sessions?limit=5').then((r) => r.data).catch(() => []),
      api.get('/me/trainer/recent-evaluations?limit=3').then((r) => r.data).catch(() => []),
    ])
      .then(([dash, ups, evs]) => {
        setData(dash);
        setUpcoming(ups || []);
        setEvaluations(evs || []);
      })
      .catch((e) => setError(e?.response?.data?.detail || 'Erreur de chargement'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-[#4F46E5]" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="soft-card p-5" data-testid="trainer-dash-error">
        <div className="flex items-center gap-2 text-[#B91C1C] mb-2">
          <AlertCircle className="w-4 h-4" />
          <span className="text-[14px] font-semibold">Espace indisponible</span>
        </div>
        <p className="text-[12.5px] text-[#4A4B63]">{error}</p>
      </div>
    );
  }

  const k = data?.kpis || {};

  return (
    <div className="space-y-4" data-testid="trainer-dashboard">
      <div>
        <div className="text-[11.5px] font-semibold uppercase tracking-wide text-[#4F46E5] mb-0.5">
          <Sparkles className="w-3 h-3 inline-block -mt-0.5 mr-1" /> Bienvenue
        </div>
        <h1 className="text-[22px] sm:text-[26px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Bonjour {data?.trainer_name?.split(' ')[0] || ''} 👋
        </h1>
        <p className="text-[12.5px] text-[#6B6C85] mt-0.5">Voici votre tableau de bord du jour.</p>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="trainer-kpis">
        <KpiCard icon={Calendar} label="Cette semaine" value={k.sessions_this_week ?? 0} suffix="sessions" color="#4F46E5" testid="kpi-sessions-week" />
        <KpiCard icon={Clock} label="Heures animées" value={k.hours_this_month ?? 0} suffix="h ce mois" color="#14B8A6" testid="kpi-hours-month" />
        <KpiCard icon={Users} label="Apprenants" value={k.active_learners ?? 0} suffix="actifs" color="#F59E0B" testid="kpi-learners" />
        <KpiCard
          icon={Star}
          label="Note moyenne"
          value={k.avg_rating != null ? k.avg_rating.toFixed(1) : '—'}
          suffix={k.evaluations_count ? `/ 5 (${k.evaluations_count} avis)` : '—'}
          color="#EC4899"
          testid="kpi-rating"
        />
      </div>

      {/* Upcoming sessions */}
      <section className="soft-card p-4" data-testid="trainer-upcoming-sessions">
        <SectionHeader title="Mes prochaines sessions" linkTo="/trainer/sessions" linkLabel="Tout voir" />
        {upcoming.length === 0 ? (
          <EmptyRow text="Aucune session à venir." />
        ) : (
          <ul className="space-y-2">
            {upcoming.slice(0, 5).map((s) => (
              <li key={s.id} className="p-3 rounded-xl border border-[#EFF0F6] bg-white flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#EEF2FF] text-[#4F46E5] flex flex-col items-center justify-center shrink-0">
                  <span className="text-[10px] font-semibold">{fmtMonth(s.startDate)}</span>
                  <span className="text-[14px] font-bold leading-none">{fmtDay(s.startDate)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">{s.title || s.program || 'Session'}</div>
                  <div className="text-[11.5px] text-[#6B6C85] truncate">
                    {s.startDate} {s.location ? `· ${s.location}` : ''} {s.mode ? `· ${s.mode}` : ''}
                  </div>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${statusStyle(s.status)}`}>{s.status || '—'}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Recent evaluations */}
      <section className="soft-card p-4" data-testid="trainer-evaluations">
        <SectionHeader title="Derniers avis stagiaires" linkTo="/trainer/evaluations" linkLabel="Tout voir" />
        {evaluations.length === 0 ? (
          <EmptyRow text="Aucune évaluation reçue pour le moment." />
        ) : (
          <ul className="space-y-2">
            {evaluations.map((ev, i) => (
              <li key={ev.id || i} className="p-3 rounded-xl border border-[#EFF0F6] bg-white">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[12.5px] font-semibold text-[#1A1B2E] truncate">{ev.learner_name || ev.author || 'Stagiaire'}</div>
                  <Stars value={ev.rating ?? ev.score ?? ev.average} />
                </div>
                {ev.comment && <p className="text-[12px] text-[#4A4B63] line-clamp-2">{ev.comment}</p>}
                <div className="text-[10.5px] text-[#8E8FA5] mt-1">
                  Session {ev.session_id || '—'}
                  {ev.created_at && <> · {new Date(ev.created_at).toLocaleDateString('fr-FR')}</>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Expiring documents */}
      {data?.expiring_documents?.length > 0 && (
        <section className="soft-card p-4 border-l-4 border-l-[#F59E0B]" data-testid="trainer-expiring-docs">
          <div className="flex items-center gap-2 mb-3">
            <FileWarning className="w-4 h-4 text-[#F59E0B]" />
            <h3 className="text-[13px] font-semibold text-[#1A1B2E]">Documents bientôt périmés</h3>
            <span className="text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#92400E]">{data.expiring_documents.length}</span>
          </div>
          <ul className="space-y-1.5">
            {data.expiring_documents.map((d) => (
              <li key={d.doc_id} className="flex items-center gap-2 text-[12px]">
                <span className="font-semibold text-[#1A1B2E] truncate flex-1">{d.type || d.name || d.doc_id}</span>
                <span className={`px-2 py-0.5 rounded text-[10.5px] font-bold ${d.days_left <= 7 ? 'bg-[#FEE2E2] text-[#B91C1C]' : 'bg-[#FEF3C7] text-[#92400E]'}`}>
                  {d.days_left === 0 ? 'Aujourd\'hui' : `J-${d.days_left}`}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// --- helpers ---
function KpiCard({ icon: Icon, label, value, suffix, color, testid }) {
  return (
    <div className="soft-card p-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10.5px] font-semibold uppercase tracking-wide text-[#8E8FA5] truncate">{label}</span>
        <Icon className="w-3.5 h-3.5 shrink-0" style={{ color }} />
      </div>
      <div className="text-[20px] font-bold text-[#1A1B2E] leading-none" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        {value}
      </div>
      <div className="text-[10.5px] text-[#8E8FA5] mt-1 truncate">{suffix}</div>
    </div>
  );
}

function SectionHeader({ title, linkTo, linkLabel }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-[14px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{title}</h3>
      {linkTo && (
        <Link to={linkTo} className="text-[11.5px] font-semibold text-[#4F46E5] hover:underline inline-flex items-center gap-0.5">
          {linkLabel} <ArrowRight className="w-3 h-3" />
        </Link>
      )}
    </div>
  );
}

function EmptyRow({ text }) {
  return <div className="py-4 text-center text-[12px] italic text-[#8E8FA5]">{text}</div>;
}

function Stars({ value }) {
  const v = Number(value || 0);
  if (!v) return <span className="text-[10.5px] text-[#8E8FA5]">—</span>;
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`w-3 h-3 ${i <= Math.round(v) ? 'fill-[#F59E0B] text-[#F59E0B]' : 'text-[#E5E7EB]'}`} />
      ))}
    </div>
  );
}

function fmtDay(iso) {
  if (!iso) return '?';
  const d = new Date(iso); if (isNaN(d.getTime())) return iso.split('-')[2] || '?';
  return d.getDate();
}
function fmtMonth(iso) {
  if (!iso) return '';
  const d = new Date(iso); if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '');
}

function statusStyle(s) {
  const k = (s || '').toLowerCase();
  if (k.includes('terminée') || k === 'finie') return 'bg-[#E6FBF7] text-[#0F766E]';
  if (k.includes('cours')) return 'bg-[#EEF2FF] text-[#4F46E5]';
  if (k.includes('annul')) return 'bg-[#FEE2E2] text-[#B91C1C]';
  return 'bg-[#FEF3C7] text-[#92400E]';
}
